import React, { useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { TableRow, TableCell } from "@mui/material";
import { useTranslation } from "react-i18next";
import { TextField, Autocomplete as RocketUIAutocomplete, IconButton } from "@totalsoft/rocket-ui";
import { DateTime } from "@ts-private/rocket-extensions";
import EditNoteIcon from "@mui/icons-material/EditNote";
import { isValid, getErrors } from '@totalsoft/pure-validations-react';
import { styled } from '@mui/material/styles'
import { isDirty } from '@totalsoft/change-tracking';
import {
  EXCHANGE_RATE_BY_BANK_ID_AND_DATE
} from "./queries";
import { useQueryWithSimpleErrorHandling } from 'hooks/apolloWrappers';

const Autocomplete = styled(RocketUIAutocomplete)(
  () => {
    return {
      ...({'& .MuiTextField-root .MuiAutocomplete-inputRoot .MuiAutocomplete-input': {
        width: 'inherit',
        minWidth: 'inherit'
      }})
    }
  }
)

const BalanceSheetListRowComponent = ({
  item,
  index,
  accStatementConfigOptions,
  currencies,
  finalizationTypes,
  balanceSheetTypes,
  onChange,
  isEditing,
  onSave,
  onDelete,
  validation,
  dirtyInfo
}) => {
  const [t, i18n] = useTranslation();
  const dimensionDate = useMemo(() => new Date(item.dimensionYear, item.dimensionMonth), [item.dimensionYear, item.dimensionMonth]);

  useQueryWithSimpleErrorHandling(EXCHANGE_RATE_BY_BANK_ID_AND_DATE, {
    variables: { bankId: item.bankId, exchangeRateDate: dimensionDate, currencyId: item.currencyId},
    onCompleted: data => {
      if (data?.exchangeRateByBankIdAndDate?.value) {
        onChange("currencyRate", data.exchangeRateByBankIdAndDate.value, index);
      }
    },
    skip: !item.inEdit || !isDirty(dirtyInfo) 
  });

  const handleEditDetails = useCallback(() => {
    window.open(`/balanceSheetDetails/${item.balanceSheetId}`);
  }, [item.balanceSheetId]);

 const dateTimeInputProps = useMemo(() => ({onKeyDown: e => { if (e.key !== "Enter" && e.key !== "Tab") e.preventDefault()}}), [])

  return (
    <TableRow>
      <TableCell>
        <TextField
          value={item.localName}
          onChange={event => onChange("localName", event, index)}
          label={t("BalanceSheet.Name")}
          disabled={!item.inEdit}
          error={item.inEdit && !isValid(validation[index].localName)}
          helperText={getErrors(validation[index].localName)}
        />
      </TableCell>
      <TableCell>
        <Autocomplete
          label={t("BalanceSheet.Configuration")}
          isSearchable={false}
          isClearable={false}
          value={accStatementConfigOptions.find(x => x.configId === item.configId)}
          valueKey="configId"
          labelKey="configName"
          options={accStatementConfigOptions}
          onChange={event => {
            onChange("configId", event.configId, index);
            onChange("bankId", event.bankId, index);
          }}
          disabled={!item.inEdit}
          error={item.inEdit && !isValid(validation[index].configId)}
          helperText={getErrors(validation[index].configId)}
        />
      </TableCell>
      <TableCell>
        <Autocomplete
          label={t("BalanceSheet.Type")}
          isSearchable={false}
          isClearable={false}
          value={balanceSheetTypes.find(x => x.dictionaryItemId === item.balanceSheetTypeId)}
          valueKey="dictionaryItemId"
          labelKey="dictionaryItemName"
          options={balanceSheetTypes}
          onChange={event => onChange("balanceSheetTypeId", event.dictionaryItemId, index)}
          disabled={!item.inEdit}
          error={item.inEdit && !isValid(validation[index].balanceSheetTypeId)}
          helperText={getErrors(validation[index].balanceSheetTypeId)}
        />
      </TableCell>
      <TableCell>
        <DateTime
          views={["year", "month"]}
          timeFormat={false}
          format={i18n.resolvedLanguage === "en" ? "en-US" : i18n.resolvedLanguage}
          value={new Date(item.dimensionYear, item.dimensionMonth)}
          label={t("BalanceSheet.DateMonth")}
          inputProps={dateTimeInputProps}
          onChange={event => onChange("dimension", event, index)}
          disabled={!item.inEdit}
        />
      </TableCell>
      <TableCell>
        <Autocomplete
          required
          isSearchable={false}
          isClearable={false}
          valueKey="currencyId"
          labelKey="code"
          value={currencies.find(x => x.currencyId === item.currencyId)}
          onChange={event => onChange("currencyId", event.currencyId, index)}
          options={currencies}
          label={t("BalanceSheet.Currency")}
          disabled={!item.inEdit}
          error={item.inEdit && !isValid(validation[index].currencyId)}
          helperText={getErrors(validation[index].currencyId)}
        />
      </TableCell>
      <TableCell>
        <TextField
          isNumeric
          decimalScale={6}
          value={item.currencyRate}
          onChange={event => onChange("currencyRate", event, index)}
          label={t("BalanceSheet.CurrencyRate")}
          disabled={!item.inEdit}
          error={item.inEdit && !isValid(validation[index].currencyRate)}
          helperText={getErrors(validation[index].currencyRate)}
        />
      </TableCell>
      <TableCell>
        <Autocomplete
          label={t("BalanceSheet.FinalizedType")}
          isSearchable={false}
          isClearable={false}
          value={finalizationTypes.find(x => x.dictionaryItemId === item.finalizationTypeId)}
          valueKey="dictionaryItemId"
          labelKey="dictionaryItemName"
          options={finalizationTypes}
          onChange={event => onChange("finalizationTypeId", event.dictionaryItemId, index)}
          disabled={!item.inEdit}
          error={item.inEdit && !isValid(validation[index].finalizationTypeId)}
          helperText={getErrors(validation[index].finalizationTypeId)}
        />
      </TableCell>
      <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
        {item.inEdit ? (
          <>
            <IconButton
              type="save"
              fontSize="small"
              color="transparent"
              size="tiny"
              tooltip={t("Save")}
              disabled={item.isSaving || !isDirty(dirtyInfo) || (validation |> getErrors) !== ""}
              onClick={() => onSave(item, index)}
            />
            <IconButton
              type="cancel"
              fontSize="small"
              color="transparent"
              size="tiny"
              tooltip={t("Close")}
              onClick={() => (item.balanceSheetId ? onChange("inEdit", false, index, item.balanceSheetId) : onDelete())}
            />
          </>
        ) : (
          <>
            <IconButton
              type="edit"
              fontSize="small"
              color="transparent"
              size="tiny"
              tooltip={t("Edit")}
              onClick={() => onChange("inEdit", true, index)}
              disabled={isEditing}
            />
            <IconButton
              type="delete"
              fontSize="small"
              color="transparent"
              size="tiny"
              tooltip={t("Delete")}
              onClick={() => onDelete(item.balanceSheetId)}
              disabled={item.isSaving}
            />
          </>
        )}
        <IconButton
          fontSize="small"
          color="transparent"
          size="tiny"
          tooltip={t("BalanceSheet.EditDetails")}
          onClick={handleEditDetails}
          disabled={!item.balanceSheetId || item.isSaving}
        >
          <EditNoteIcon />
        </IconButton>
      </TableCell>
    </TableRow>
  );
};

BalanceSheetListRowComponent.propTypes = {
  item: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  accStatementConfigOptions: PropTypes.array.isRequired,
  currencies: PropTypes.array.isRequired,
  balanceSheetTypes: PropTypes.array.isRequired,
  finalizationTypes: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  isEditing: PropTypes.bool.isRequired,
  validation: PropTypes.object.isRequired,
  dirtyInfo: PropTypes.oneOfType([ PropTypes.bool, PropTypes.object ])
};

export default BalanceSheetListRowComponent;
